// The Manager Entry form's data calls, kept out of the component so they can
// be tested without rendering anything — the same split moneyWrites.ts uses.
//
// Nothing here re-implements a server rule. The forbidden pay codes, the
// roster check, the closed-period refusal and both halves of idempotency all
// live in src/manager_entry.js and db/migrations/022_manager_entry.sql; a
// client-side copy of a server rule is a copy that drifts. What this module
// does is send the shape the endpoint expects and hand back what it said.
//
// THE SIX SECTIONS ARE NOT DEFINED HERE. They come down the wire from
// `GET /api/v1/payroll/manager-entry`, because the pay codes they carry are
// what ADP reads and two copies of that list is how one of them eventually
// gains a seventh entry nobody ruled on.
import { authedGet, authedWrite } from './apiClient';
import { persistResource } from './store';
import type { RaiseDecisionInfo } from './hourRaises';

export interface EntrySection {
  key: string;
  payCode: string;
  kind: 'EARNING' | 'DEDUCTION';
  category: string;
  title: string;
  detailLabel: string;
  help: string;
}

export interface EntryPayPeriod {
  id: number;
  periodCode: string;
  startDate: string;
  endDate: string;
  adpWeek: number | null;
  closed: boolean;
  // docs/payroll/app-period-dates-per-clinic.md: present only when the
  // caller sees all four clinics at once (an admin with none selected
  // server-side) — `startDate`/`endDate` above are already this clinic's
  // own dates whenever the caller is scoped to exactly one location.
  datesByCompany?: Record<string, { startDate: string; endDate: string }>;
}

export interface EntrySubmission {
  id: number;
  locationCode: string;
  payPeriodId: number;
  // Null when private bonus lines (097) were taken out of a manager's view of
  // this submission — the hash would cover a line they cannot see.
  contentHash: string | null;
  lineCount: number;
  rosterCount: number;
  earningTotal: string;
  deductionTotal: string;
  submittedBy: string;
  submittedAt: string;
}

export interface EntryLine {
  id: number;
  submissionId: number;
  kind: 'EARNING' | 'DEDUCTION';
  payPeriodId: number;
  employeeId: number;
  employeeName: string | null;
  fileNo: string | null;
  locationCode: string | null;
  category: string;
  payCode: string;
  amount: string;
  reason: string;
  note: string | null;
  enteredBy: string;
  enteredAt: string;
  // Set when the line has been VOIDED (026). A voided line still comes down
  // the wire — the manager who voided one by mistake needs to see that it
  // happened — but it counts toward no total anywhere.
  voidedAt: string | null;
  voidedBy: string | null;
  /** 097: an owner/director-only bonus line. The server never sends one to
   * anybody else, so a manager's lines are always false (or absent). */
  private?: boolean;
}

// --- roster (new hire / termination / rate change) -------------------------
//
// SAME DISCIPLINE AS THE SIX MONEY SECTIONS ABOVE: `rosterSections` comes
// down the wire from the same endpoint, never hard-coded here, for the same
// reason — a second copy of the section list is how one of them drifts.

export interface RosterExtraField {
  key: string;
  label: string;
  /** 'date' draws a date picker (Date of birth); absent = a text box. */
  type?: 'date' | 'text';
}

export interface RosterSection {
  key: 'new_hires' | 'terminations' | 'rate_changes' | 'misc';
  changeType: 'NEW_HIRE' | 'TERMINATION' | 'RATE_CHANGE' | 'DIRECT_DEPOSIT' | 'MISC';
  title: string;
  field1Label: string;
  field2Label: string;
  help: string;
  secure: boolean;
  extraFields: RosterExtraField[];
}

export interface RosterChange {
  id: number;
  submissionId: number;
  payPeriodId: number;
  locationCode: string;
  employeeId: number | null;
  employeeName: string | null;
  fileNo: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  changeType: 'NEW_HIRE' | 'TERMINATION' | 'RATE_CHANGE' | 'DIRECT_DEPOSIT' | 'MISC';
  field1: string;
  field2: string;
  note: string | null;
  extra: Record<string, unknown> | null;
  // Whether a secure value was on file — NEVER the value itself. This is
  // the one field on this whole page allowed to say "SSN on file"; nothing
  // downstream may ever be handed the SSN or bank details back.
  ssnProvided: boolean;
  bankProvided: boolean;
  enteredBy: string;
  enteredAt: string;
  voidedAt: string | null;
  voidedBy: string | null;
  /** "Completed" — done in ADP (owner, 2026-10-06; column from 078). */
  enteredInAdpAt?: string | null;
  enteredInAdpBy?: string | null;
  /** N-088 (Request Ledger, owner 2026-09-08): set only on a TERMINATION row,
   * computed server-side from payroll_line for that employee and period —
   * whether ADP termination can happen now or must wait for the next cycle.
   * null/absent on every other change type, and on a termination the server
   * could not judge (should not happen once `employeeId` is set). Optional
   * so every fixture that predates this field still type-checks. */
  payVerdict?: PayVerdict | null;
  /** Owner, 2026-09-23: the ADP confirmation of a TERMINATION — absent from
   * the import, then the register, for the first period after the last day
   * (src/termination_check.js). null on every other change type. */
  adpCheck?: AdpCheck | null;
  /** Last-4 masks of the SSN / bank details on file for a new hire or SSN &
   * direct deposit row (owner, 2026-09-24); null when none were entered. */
  secureTail?: SecureTail | null;
}

/** Whether a new hire's / direct deposit's details are on file. No digits:
 * the list is an unlogged read (Cursor review #35); the values come only
 * through the logged Show. */
export interface SecureTail {
  onFile: true;
}

export interface AdpCheck {
  code: 'CONFIRMED' | 'WAITING_IMPORT' | 'WAITING_REGISTER' | 'FLAG' | 'ERROR';
  /** The full sentence — which week, which file. */
  text: string;
  /** The person's status in the app right now. */
  status: string | null;
}

export interface PayVerdict {
  code: 'PAID_NEXT_CYCLE' | 'NO_PAY_NOW' | 'NOT_IMPORTED';
  label: string;
}

export interface PendingRaise {
  employeeId: number;
  employeeName: string;
  fileNo: string | null;
  role: string;
  locationCode: string;
  serviceHours: number;
  currentRate: string | null;
  // Present when this raise still shows despite a past owner decision — a
  // DEFERRED one arriving at its target period, or a REJECTED one that
  // crossed its next 1,000-hour threshold (docs/payroll/app-hour-raise-
  // decisions.md).
  decision?: RaiseDecisionInfo | null;
}

/** Standard 1,000-hour raise. The box starts at current + this, and they can change it. */
export const HOUR_RAISE_STEP = 0.5;

export function suggestedRaiseRate(current: string | null | undefined): string {
  const n = Number(current);
  if (!Number.isFinite(n) || n <= 0) return '';
  return (n + HOUR_RAISE_STEP).toFixed(2);
}

export interface ManagerEntryData {
  submissions: EntrySubmission[];
  lines: EntryLine[];
  sections: EntrySection[];
  payPeriods: EntryPayPeriod[];
  rosterSections: RosterSection[];
  rosterChanges: RosterChange[];
  pendingRaises?: PendingRaise[];
  followingPeriod?: EntryPayPeriod | null;
  /** Owner/director only: Candace's employee id per clinic (she is not on the
   * People list) — for the "Candace bonus" choice (owner, 2026-10-06). */
  candace?: Record<string, number>;
}

/** "Candace bonus" (owner, 2026-10-06): a bonus line for Candace, always
 * private, offered to the owner and director only. On the wire it is an
 * ordinary private bonus line. Pure. */
export const CANDACE_BONUS = 'candace_bonus';
export function asCandaceBonus<T extends { what: string; private?: boolean; detail?: string }>(line: T): T {
  if (line.what !== CANDACE_BONUS) return line;
  return { ...line, what: 'bonus', private: true, detail: String(line.detail ?? '').trim() || 'Candace bonus' };
}

/** One row a manager typed: who, how much, and what for. */
export interface EntryRowInput {
  employeeId: number | '';
  amount: string;
  detail: string;
  note: string;
}

/**
 * One roster row a manager typed. `extra` and the four secure fields are
 * only ever populated for `new_hires` — the other two sections leave them
 * blank and toRosterEntriesPayload never sends them for those keys.
 *
 * The secure fields live ONLY in this component's state and in the outgoing
 * request body — never in localStorage/IndexedDB, and never echoed back
 * after submit (see successMessage, which is built from counts, not values).
 */
export interface RosterRowInput {
  employeeId: number | '';
  firstName: string;
  middleName: string;
  lastName: string;
  field1: string;
  field2: string;
  note: string;
  extra: Record<string, string>;
  /** Set only when the New hire line was prefilled from the Recruiting board (?candidate=). */
  candidateId?: number | null;
  ssn: string;
  bankName: string;
  routingNumber: string;
  accountNumber: string;
}

export interface SubmitResult {
  submission: EntrySubmission;
  linesWritten?: number;
  rosterWritten?: number;
  duplicate: boolean;
  replayed?: boolean;
  message?: string;
  /** A raise was filed but its pay-rate row did not save (row 19). */
  rateWarning?: string;
}

export const MANAGER_ENTRY_KEY = 'manager-entry';

export async function loadManagerEntry(payPeriodId: number | null): Promise<ManagerEntryData> {
  const q = payPeriodId === null ? '' : `?payPeriod=${payPeriodId}`;
  const data = await authedGet<ManagerEntryData>(`/api/v1/payroll/manager-entry${q}`);
  if (payPeriodId === null) await persistResource(MANAGER_ENTRY_KEY, data);
  return data;
}

/**
 * Turn the form's rows into the `entries` object the endpoint takes, dropping
 * rows the manager started and left blank.
 *
 * A row is BLANK — and silently skipped — only when it has neither an employee
 * nor an amount. A half-filled row is passed through deliberately, so the
 * server refuses it by name ("no valid amount") rather than this function
 * quietly deciding the manager did not mean it. Dropping a half-filled row is
 * how an entry vanishes without anyone being told.
 */
export function toEntriesPayload(
  rowsBySection: Record<string, EntryRowInput[]>,
): Record<string, Array<{ employeeId: number | ''; amount: string; detail: string; note: string }>> {
  const entries: Record<string, Array<{ employeeId: number | ''; amount: string; detail: string; note: string }>> = {};
  for (const [key, rows] of Object.entries(rowsBySection)) {
    const kept = rows.filter((r) => r.employeeId !== '' || r.amount.trim() !== '');
    if (kept.length) {
      entries[key] = kept.map((r) => ({
        employeeId: r.employeeId,
        amount: r.amount.trim(),
        detail: r.detail.trim(),
        note: r.note.trim(),
      }));
    }
  }
  return entries;
}

/** How many rows this submission will actually send. */
export function countRows(rowsBySection: Record<string, EntryRowInput[]>): number {
  return Object.values(toEntriesPayload(rowsBySection)).reduce((n, rows) => n + rows.length, 0);
}

/**
 * Turn the form's roster rows into the shape the endpoint takes.
 *
 * Same "half-filled is sent, not dropped" rule as toEntriesPayload — a row
 * counts as started when it has an employee OR either date/amount field
 * typed into it. The four secure fields and `extra` are only ever attached
 * for `new_hires`; the other two sections never carry them, whether or not
 * the manager typed anything (there is nowhere on those rows to type it).
 * A blank secure field is sent as '' — the server accepts that (SSN/bank
 * are optional per row) — never omitted vs. sent inconsistently.
 */
export function toRosterEntriesPayload(
  rowsBySection: Record<string, RosterRowInput[]>,
): Record<string, Array<Record<string, unknown>>> {
  const entries: Record<string, Array<Record<string, unknown>>> = {};
  for (const [key, rows] of Object.entries(rowsBySection)) {
    const kept = rows.filter(
      (r) => r.employeeId !== '' || String(r.field1 ?? '').trim() !== '' || String(r.field2 ?? '').trim() !== ''
        || (r.firstName ?? '').trim() !== '' || (r.lastName ?? '').trim() !== '',
    );
    if (!kept.length) continue;
    entries[key] = kept.map((r) => {
      const out: Record<string, unknown> = {
        employeeId: r.employeeId,
        field1: String(r.field1 ?? '').trim(),
        field2: String(r.field2 ?? '').trim(),
        note: String(r.note ?? '').trim(),
      };
      if (key === 'direct_deposit') {
        // Direct deposit update (owner, 2026-09-24): a person on the roster
        // and their new bank details — no SSN.
        out.bankName = r.bankName.trim();
        out.routingNumber = r.routingNumber.trim();
        out.accountNumber = r.accountNumber.trim();
      }
      if (key === 'new_hires') {
        out.firstName = (r.firstName ?? '').trim();
        out.middleName = (r.middleName ?? '').trim();
        // The Recruiting board's candidate id rides along so payroll close can
        // link the hire to the candidate without a name match (src/manager_entry.js).
        if (r.candidateId) out.candidateId = r.candidateId;
        out.lastName = (r.lastName ?? '').trim();
        out.ssn = r.ssn.trim();
        out.bankName = r.bankName.trim();
        out.routingNumber = r.routingNumber.trim();
        out.accountNumber = r.accountNumber.trim();
        for (const [fieldKey, value] of Object.entries(r.extra)) {
          out[fieldKey] = value.trim();
        }
      }
      return out;
    });
  }
  return entries;
}

/** How many roster rows this submission will actually send. */
export function countRosterRows(rowsBySection: Record<string, RosterRowInput[]>): number {
  return Object.values(toRosterEntriesPayload(rowsBySection)).reduce((n, rows) => n + rows.length, 0);
}

/**
 * The line a manager sees after a successful submit.
 *
 * Built from COUNTS only, never from the rows themselves — that is what
 * guarantees an SSN, routing number or account number can never end up in
 * this string, by construction rather than by remembering to scrub it.
 */
export function successMessage(linesWritten: number, rosterWritten: number): string {
  return `Submitted ${linesWritten} line(s) and ${rosterWritten} roster change(s).`;
}

/**
 * Submit the batch.
 *
 * `key` is passed in by the caller and reused across retries of the SAME
 * submission — that is what makes a double-click replay the first result
 * instead of paying someone twice. A new batch gets a new key.
 */
export function submitManagerEntry(
  input: { locationCode: string; payPeriodId: number; entries: Record<string, unknown[]> },
  key: string,
): Promise<SubmitResult> {
  return authedWrite<SubmitResult>('POST', '/api/v1/payroll/manager-entry', input, key);
}

// --- void (026_entry_void.sql) ---------------------------------------------
//
// Owner ask, 2026-08-15: delete or correct an item already submitted. The
// server soft-voids — the row stays, `voidedAt` is set — and an EDIT is a
// void plus a fresh one-line submission, so a correction goes through exactly
// the same guards an original does. Nothing here re-implements any of that.

/** Which table the line lives in. The endpoint accepts these three and
 * nothing else; a fourth value never reaches SQL (server-side F-37). */
export type VoidTarget = 'earnings' | 'deductions' | 'roster';

/** The URL segment for a money line, from the kind the read already returns —
 * so a caller never has to know which extras table a pay code lands in. */
export function voidTargetForLine(line: Pick<EntryLine, 'kind'>): VoidTarget {
  return line.kind === 'DEDUCTION' ? 'deductions' : 'earnings';
}

export interface VoidResult {
  voided: boolean;
  replayed?: boolean;
  version?: number;
}

export function voidEntryLine(target: VoidTarget, id: number, key: string): Promise<VoidResult> {
  return authedWrite<VoidResult>(
    'POST', `/api/v1/payroll/manager-entry/${target}/${id}/void`, {}, key,
  );
}

export interface MoveResult {
  moved: boolean;
  replayed?: boolean;
  move?: { oldId: number; newId: number };
}

/** Idea 34: move an earning/deduction line to another OPEN pay period.
 * Owner and director only (the server refuses a manager). */
export function moveEntryLine(
  target: 'earnings' | 'deductions', id: number, targetPayPeriodId: number, key: string,
): Promise<MoveResult> {
  return authedWrite<MoveResult>(
    'POST', `/api/v1/payroll/manager-entry/${target}/${id}/move`, { targetPayPeriodId }, key,
  );
}

export interface SecureDetails {
  ssn: string | null;
  bankName: string | null;
  routingNumber: string | null;
  accountNumber: string | null;
}

/** The full SSN / bank details (owner, director, or that clinic's manager).
 * A fresh key every click — as many clicks as anyone wants. */
export function revealRosterSecure(id: number): Promise<SecureDetails> {
  return authedWrite<SecureDetails>(
    'POST', `/api/v1/payroll/manager-entry/roster/${id}/secure`, {}, crypto.randomUUID(),
  );
}

export function correctRosterChange(
  id: number,
  body: { field1: string; field2: string; note: string },
  key: string,
): Promise<{ corrected: boolean; replayed?: boolean }> {
  return authedWrite('POST', `/api/v1/payroll/manager-entry/roster/${id}/correct`, body, key);
}

// --- the one-line form ------------------------------------------------------
//
// Owner redesign, 2026-08-15: instead of nine sections each with their own row
// list and one Submit at the bottom, ONE line — what / who / amount / detail —
// submitted on its own, with the items filling the lists underneath. Each
// submission is a one-row batch, which changes nothing server-side: a one-line
// batch is still a batch, still atomic, still idempotent by key.

/** What the single line is: one of the six money sections or one of the three
 * roster ones, named by the same key the server sent down. */
export interface OneLineInput {
  what: string;
  employeeId: number | '';
  firstName: string;
  middleName: string;
  lastName: string;
  amount: string;
  detail: string;
  note: string;
  // Roster-only. Unused (and never sent) when `what` is a money section.
  field1: string;
  field2: string;
  extra: Record<string, string>;
  /** Set only when the New hire line was prefilled from the Recruiting board (?candidate=). */
  candidateId?: number | null;
  /** Bonus lines only, owner/director only (097): hidden from clinic managers.
   * The server refuses it from anyone else and on any other section. */
  private?: boolean;
  ssn: string;
  bankName: string;
  routingNumber: string;
  accountNumber: string;
}

export function blankOneLine(what = ''): OneLineInput {
  return {
    what,
    employeeId: '', firstName: '', middleName: '', lastName: '',
    amount: '', detail: '', note: '',
    field1: '', field2: '', extra: {},
    ssn: '', bankName: '', routingNumber: '', accountNumber: '',
  };
}

/**
 * The `entries` object for a ONE-line submission.
 *
 * Money and roster sections take different shapes, and this is the single
 * place that decides which — the component never builds a payload itself.
 * It reuses toEntriesPayload / toRosterEntriesPayload rather than repeating
 * their trim-and-keep rules, so a half-filled line still reaches the server
 * and is refused BY NAME instead of being silently dropped here.
 */
const ROSTER_WHATS = new Set(['new_hires', 'terminations', 'rate_changes', 'direct_deposit', 'misc']);

export function toOneLinePayload(
  line: OneLineInput,
  isRoster: boolean,
): Record<string, unknown[]> {
  if (!line.what) return {};
  if (isRoster || ROSTER_WHATS.has(line.what)) {
    return toRosterEntriesPayload({
      [line.what]: [{
        employeeId: line.employeeId,
        firstName: line.firstName,
        middleName: line.middleName,
        lastName: line.lastName,
        field1: line.field1,
        field2: line.field2,
        note: line.note,
        extra: line.extra,
        candidateId: line.candidateId ?? null,
        ssn: line.ssn,
        bankName: line.bankName,
        routingNumber: line.routingNumber,
        accountNumber: line.accountNumber,
      }],
    }) as Record<string, unknown[]>;
  }
  const entries = toEntriesPayload({
    [line.what]: [{
      employeeId: line.employeeId,
      amount: line.amount,
      detail: line.detail,
      note: line.note,
    }],
  }) as Record<string, Array<Record<string, unknown>>>;
  // Sent only when set, and only on a bonus line — an ordinary line's body is
  // exactly what it was before 097.
  if (line.private === true && line.what === 'bonus' && entries.bonus) {
    entries.bonus = entries.bonus.map((r) => ({ ...r, private: true }));
  }
  return entries as Record<string, unknown[]>;
}

/** Whether Submit should be enabled: a `what`, a person, and at least one
 * value typed. The server decides for real — this only stops a submission
 * that is certain to be refused. */
export function oneLineIsSendable(line: OneLineInput, isRoster: boolean): boolean {
  if (!line.what) return false;
  const roster = isRoster || ROSTER_WHATS.has(line.what);
  const whoOk = line.what === 'new_hires'
    ? String(line.firstName ?? '').trim() !== '' && String(line.lastName ?? '').trim() !== ''
    : line.employeeId !== '';
  if (!whoOk) return false;
  if (line.what === 'rate_changes') {
    const rate = Number(line.field2);
    return Number.isFinite(rate) && rate > 0;
  }
  if (line.what === 'direct_deposit') {
    return String(line.routingNumber ?? '').trim() !== '' && String(line.accountNumber ?? '').trim() !== '';
  }
  return roster
    ? String(line.field1 ?? '').trim() !== '' || String(line.field2 ?? '').trim() !== ''
    : String(line.amount ?? '').trim() !== '';
}
